import { Block } from '@vedalib/editor'
import cn from 'classnames'
import React, { useCallback, useMemo, useRef, useState } from 'react'

import ScaledBlockViewport from 'components/Editor/cource/renderEntry/ScaledBlockViewport'
import { IconButton } from 'components/uiKit/Button'
import { Tooltip } from 'components/uiKit/Dropdown'
import DropdownMenu from 'components/uiKit/DropdownMenu/DropdownMenu'
import Highlighter from 'components/uiKit/Highlighter'
import Icon from 'components/uiKit/Icon'
import { KitSize } from 'components/uiKit/KitTypes'
import type { IMenuOption } from 'components/uiKit/Menu'
import type { EmployeePreferencesTab, TemplatesListSchemaFragment } from 'gql/__generated__/graphql'
import type { TemplateActionsUiEnum } from 'routes/routes/App/routes/Company/routes/Templates/components/enum'
import { BlockMode, DeviceMode } from 'services/Store/Project/enums'
import { getEnumLabel } from 'utils/enum'
import { testProps, testPropsEl } from 'utils/test/qaData'

import FavoriteIcon from '../FavoriteIcon'
import type { IItemProps } from '../MasonryTemplate/MasonryTemplate'
import Numeration from '../Numeration'
import { TemplatePickerMode } from '../types'
import * as s from './CellRendererBlock.module.scss'

interface ICellRendererBlockProps extends Omit<IItemProps, 'onClick' | 'tab'> {
  template: TemplatesListSchemaFragment
  index: number
  onHeight?: (id: string, height: number) => void
  width?: number
  mode: TemplatePickerMode
  tab?: EmployeePreferencesTab
  actions?: IMenuOption<TemplateActionsUiEnum>[]
  onClickActions?: (value: string, uuid: string) => void
  companyId: string
  projectId?: string
  showFavorite?: boolean
  allSelected?: string[]
  testData?: string
}

const DEFAULT_WIDTH = 280

const CellRendererBlock: React.FC<ICellRendererBlockProps> = ({
  template,
  search,
  index,
  onHeight,
  selected,
  mode,
  removeByIndex,
  width = DEFAULT_WIDTH,
  tab,
  actions,
  onClickActions,
  companyId,
  projectId,
  showFavorite,
  allSelected,
  testData,
}) => {
  const { name, isFavorite } = template
  const id = template.id || (template as Block).uuid
  const blocks = useMemo(() => template.blocks || [template], [template])

  const [isHovered, setIsHovered] = useState(false)
  const blockHeights = useRef<Record<number, number>>({})

  const validBlocks = useMemo(
    () =>
      blocks?.filter((block) => {
        if (!block || !block.schema) {
          return false
        }
        const schema = block.schema
        return schema && typeof schema === 'object' && schema.rootId && schema.nodes
      }) || [],
    [blocks],
  )
  const totalBlocksCount = validBlocks.length

  const handleMouseEnter = () => {
    setIsHovered(true)
  }
  const handleMouseLeave = () => {
    setIsHovered(false)
  }

  const onHeightCb = useCallback(
    (blockIndex: number) => (blockHeight: number) => {
      blockHeights.current[blockIndex] = blockHeight

      const calculatedHeights = Object.keys(blockHeights.current).length
      if (calculatedHeights === totalBlocksCount && onHeight) {
        const totalHeight = Object.values(blockHeights.current).reduce((sum, h) => sum + h, 0)
        onHeight(id, totalHeight + 70)
      }
    },
    [onHeight, id, totalBlocksCount],
  )

  const isSelected = Boolean(allSelected?.find((templateId) => id === templateId))

  return (
    <div className={s.root} {...testProps({ el: 'templateCard', name, testData })}>
      <div
        className={cn(s.card, {
          [s.active]: isSelected,
          [s.hovered]: isHovered,
          [s.withActions]: actions,
        })}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className={s.bottomGroup}>
          <Tooltip overlay={name} fluid>
            <div className={s.header}>
              <div
                {...testPropsEl('blockCardTitle', { value: id }, { index })}
                className={s.blockTitle}
              >
                <Highlighter
                  search={search}
                  text={name || getEnumLabel('BlockModeEnum', BlockMode.view)}
                />
              </div>
              {totalBlocksCount > 1 && (
                <div className={s.badge}>
                  <Icon name='mainMenuProjects' size={KitSize.XS} />
                  <span>{totalBlocksCount}</span>
                </div>
              )}
            </div>
          </Tooltip>
          {actions && (
            <div className={s.actions}>
              <DropdownMenu
                name={`${name}.actionsDropdownMenu`}
                onClick={({ value }) => onClickActions?.(value, id)}
                onVisibleChange={setIsHovered}
                options={actions}
                placement='bottom-end'
              >
                <IconButton
                  icon='iconsOtherMore'
                  name={`${name}.actions`}
                  size={KitSize.S}
                  styleType='ghost'
                />
              </DropdownMenu>
            </div>
          )}
        </div>
        <div className={s.blockWrapper}>
          <div {...testPropsEl('blockCardContent', { value: id, name }, { index })}>
            {validBlocks.map((block, blockIndex) => (
              <ScaledBlockViewport
                block={block}
                deviceMode={DeviceMode.desktop}
                key={blockIndex}
                onHeight={onHeightCb(blockIndex)}
                viewportSize={width - 55}
              />
            ))}
          </div>
        </div>
        {mode === TemplatePickerMode.multi && (
          <Numeration removeByIndex={removeByIndex} selected={selected} uuid={id} />
        )}
        {showFavorite && (
          <div className={cn(s.favIcon, { [s.favIconSelected]: isFavorite })}>
            <FavoriteIcon
              companyId={companyId}
              projectId={projectId}
              tab={tab}
              template={template}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default CellRendererBlock
